import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  ModalBody,
  ModalFooter,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MultiSelect } from 'react-multi-select-component';
import { useQueryClient } from 'react-query';

import { SetToggleThemeContext } from '../../../../contexts/SetToggleThemeContext';
import { BI_CATALOG_KEY, useBiGroups } from '../../../../hooks/useBi';
import {
  BiConfig,
  BiPermission,
  BiReport,
  BiUserOption,
  biErrorMessage,
  biService,
} from '../../../../services/biService';
import { resolveAppearance } from '../../../../utils/biAppearance';
import { Internationalization } from '../../../../utils/InternationalizationMultSelect';
import { parsePowerBiLink } from '../../../../utils/powerBiLink';
import { apllyToast } from '../../../Global/Toast2.0';
import { AppearancePicker } from '../../BI/AppearancePicker';
import { ReportCard } from '../../BI/ReportCard';
import { Container as MultiSelectContainer } from '../ModalAddAction/styles';
import { ModalComponent } from '..';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  report?: BiReport;
  groups: string[];
  configs: BiConfig[];
  permissions: BiPermission[];
  users: BiUserOption[];
}

interface Option {
  value: string;
  label: string;
}

type Errors = Partial<
  Record<'link' | 'reportName' | 'group' | 'config', string>
>;

const SUMMARY_MAX = 160;

const EMPTY_FORM = {
  link: '',
  reportName: '',
  groupNameSamasc: '',
  groupId: '',
  embededId: '',
  embededUrl: '',
  id_power_bi_configs: '',
  summary: '',
  description: '',
  refresh_frequency: '',
  glossary: '',
  owner_id: '',
  display_order: '0',
  status: true,
  card_color: null as string | null,
  card_icon: null as string | null,
};

type FormState = typeof EMPTY_FORM;
type TextField = Exclude<
  keyof FormState,
  'status' | 'card_color' | 'card_icon'
>;

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Box as="section">
      <Text fontWeight="bold" color="gray.50">
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500">
        {description}
      </Text>
      <Stack spacing="4" mt="4">
        {children}
      </Stack>
    </Box>
  );
}

export function ModalReportBI({
  isOpen,
  onClose,
  report,
  groups,
  configs,
  permissions,
  users,
}: Props) {
  const queryClient = useQueryClient();
  const { toggleTheme } = useContext(SetToggleThemeContext);
  const { byName } = useBiGroups();
  const technical = useDisclosure();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [selectedPermissions, setSelectedPermissions] = useState<Option[]>([]);
  // Na edição, só é possível salvar depois de carregar as permissões atuais:
  // salvar com a lista vazia deixaria o relatório aberto para todos.
  const [permissionsState, setPermissionsState] = useState<
    'loading' | 'ready' | 'error'
  >('ready');
  const [errors, setErrors] = useState<Errors>({});
  const [saving, setSaving] = useState(false);
  // Relatório que a resposta das permissões precisa corresponder; descarta
  // respostas atrasadas de outro relatório aberto antes.
  const expectedReportIdRef = useRef<string | undefined>();
  // Id do relatório já criado nesta abertura do modal: uma nova tentativa
  // depois de falha atualiza o mesmo cadastro em vez de duplicar.
  const savedIdRef = useRef<string | undefined>();

  const permissionOptions: Option[] = permissions.map(permission => ({
    value: permission.id,
    label: permission.identification || permission.name,
  }));

  // A lista de permissões pode chegar depois de abrir o modal: mostra o nome
  // assim que ela estiver disponível.
  const selectedPermissionOptions = selectedPermissions.map(
    selected =>
      permissionOptions.find(option => option.value === selected.value) ||
      selected,
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setErrors({});
    technical.onClose();
    setSelectedPermissions([]);
    savedIdRef.current = undefined;
    expectedReportIdRef.current = report?.id;

    if (!report) {
      setForm({
        ...EMPTY_FORM,
        id_power_bi_configs: configs.length === 1 ? configs[0].id : '',
      });
      setPermissionsState('ready');
      return;
    }

    setForm({
      link: '',
      reportName: report.reportName,
      groupNameSamasc: report.groupNameSamasc || '',
      groupId: report.groupId,
      embededId: report.embededId,
      embededUrl: report.embededUrl,
      id_power_bi_configs: report.id_power_bi_configs || '',
      summary: report.summary || '',
      description: report.description || '',
      refresh_frequency: report.refresh_frequency || '',
      glossary: report.glossary || '',
      owner_id: report.owner?.id || '',
      display_order: String(report.display_order || 0),
      status: report.status,
      card_color: report.card_color || null,
      card_icon: report.card_icon || null,
    });

    loadPermissions(report.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, report?.id]);

  // Usa os ids gravados no vínculo, não a identificação: a identificação
  // não é única na tabela de permissões.
  function loadPermissions(reportId: string) {
    setPermissionsState('loading');
    biService
      .listReportPermissionIds(reportId)
      .then(ids => {
        if (expectedReportIdRef.current !== reportId) {
          return;
        }

        setSelectedPermissions(ids.map(id => ({ value: id, label: id })));
        setPermissionsState('ready');
      })
      .catch(() => {
        if (expectedReportIdRef.current === reportId) {
          setPermissionsState('error');
        }
      });
  }

  const handleRetryPermissions = () => {
    if (report) {
      loadPermissions(report.id);
    }
  };

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const bindField =
    (field: TextField) =>
    (
      event: ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) =>
      update(field, event.target.value);

  const handleLinkChange = (value: string) => {
    update('link', value);

    const parsed = parsePowerBiLink(value);

    if (parsed) {
      setForm(current => ({
        ...current,
        link: value,
        groupId: parsed.groupId,
        embededId: parsed.reportId,
        embededUrl: parsed.embedUrl,
      }));
      setErrors(current => ({ ...current, link: undefined }));
    }
  };

  function validate(): Errors {
    const found: Errors = {};

    if (form.link.trim() && !parsePowerBiLink(form.link)) {
      found.link =
        'Link não reconhecido. Copie o endereço do relatório aberto no Power BI.';
    } else if (!form.groupId || !form.embededId || !form.embededUrl) {
      found.link = 'Cole o link do relatório no Power BI.';
    }

    if (!form.reportName.trim()) {
      found.reportName = 'Informe o nome do relatório.';
    }

    if (!form.groupNameSamasc.trim()) {
      found.group = 'Informe o grupo.';
    }

    if (!form.id_power_bi_configs) {
      found.config = 'Escolha a configuração de BI.';
    }

    return found;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const found = validate();
    setErrors(found);

    if (Object.keys(found).length > 0) {
      if (found.link && form.groupId) {
        technical.onOpen();
      }
      return;
    }

    if (permissionsState !== 'ready') {
      return;
    }

    const fields = {
      reportName: form.reportName.trim(),
      groupNameSamasc: form.groupNameSamasc.trim(),
      groupId: form.groupId.trim(),
      embededId: form.embededId.trim(),
      embededUrl: form.embededUrl.trim(),
      id_power_bi_configs: form.id_power_bi_configs,
      summary: form.summary.trim(),
      description: form.description,
      refresh_frequency: form.refresh_frequency,
      glossary: form.glossary,
      owner_id: form.owner_id || null,
      display_order: Math.min(
        9999,
        Math.max(0, Math.trunc(Number(form.display_order) || 0)),
      ),
      card_color: form.card_color,
      card_icon: form.card_icon,
    };
    const permissionsIds = selectedPermissions.map(option => option.value);

    setSaving(true);
    try {
      if (report) {
        await biService.updateReport(report.id, {
          ...fields,
          status: form.status,
        });
        await biService.syncReportPermissions(report.id, permissionsIds);
      } else {
        // Relatório novo nasce desativado e só é ativado depois de receber
        // as permissões: sem permissão vinculada ele seria visível para todos.
        let id = savedIdRef.current;

        if (id) {
          await biService.updateReport(id, { ...fields, status: false });
        } else {
          const created = await biService.createReport({
            ...fields,
            groupName: fields.groupNameSamasc,
            status: false,
          });

          id = created.id;
          savedIdRef.current = id;
        }

        await biService.syncReportPermissions(id, permissionsIds);

        if (form.status) {
          await biService.updateReport(id, { status: true });
        }
      }

      await queryClient.invalidateQueries(BI_CATALOG_KEY);
      apllyToast(
        'success',
        report ? 'Relatório atualizado.' : 'Relatório publicado.',
      );
      onClose();
    } catch (err) {
      apllyToast(
        'error',
        biErrorMessage(err, 'Não foi possível salvar o relatório.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAppearanceChange = (value: {
    color: string | null;
    icon: string | null;
  }) => {
    setForm(current => ({
      ...current,
      card_color: value.color,
      card_icon: value.icon,
    }));
  };

  const parsedLink = form.link.trim() ? parsePowerBiLink(form.link) : null;
  const groupStyle = byName[form.groupNameSamasc.trim() || 'Sem grupo'];
  const appearance = resolveAppearance(
    { card_color: form.card_color, card_icon: form.card_icon },
    groupStyle,
    form.groupNameSamasc,
  );

  const preview = {
    ...(report || {}),
    id: 'preview',
    reportName: form.reportName.trim() || 'Nome do relatório',
    groupNameSamasc: form.groupNameSamasc.trim(),
    summary: form.summary.trim() || null,
    description: form.description.trim() || null,
    refresh_frequency: form.refresh_frequency.trim() || null,
    isFavorite: false,
  } as BiReport;

  return (
    <ModalComponent
      title={report ? 'Editar relatório' : 'Adicionar nova análise'}
      isOpen={isOpen}
      onClose={onClose}
      sizeSpecifyc="3xl"
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      {/* O form ocupa a altura do modal para o corpo rolar por dentro e o
          rodapé com os botões ficar sempre visível. */}
      <Box
        as="form"
        onSubmit={handleSubmit}
        display="flex"
        flexDirection="column"
        flex="1"
        minH="0"
      >
        <ModalBody>
          <Stack spacing="6" divider={<Divider borderColor="gray.600" />}>
            <Section
              title="Relatório no Power BI"
              description="De onde o SAMASC busca o relatório."
            >
              <FormControl isInvalid={!!errors.link}>
                <FormLabel htmlFor="bi-link">
                  Link do relatório no Power BI
                </FormLabel>
                <Input
                  id="bi-link"
                  value={form.link}
                  onChange={event => handleLinkChange(event.target.value)}
                  placeholder="https://app.powerbi.com/groups/.../reports/..."
                />
                {parsedLink ? (
                  <FormHelperText color="green.500">
                    Link reconhecido: o workspace e o relatório foram
                    preenchidos.
                  </FormHelperText>
                ) : (
                  <FormHelperText>
                    {report
                      ? 'Deixe em branco para manter o relatório atual.'
                      : 'Abra o relatório no Power BI e copie o endereço do navegador.'}
                  </FormHelperText>
                )}
                <FormErrorMessage>{errors.link}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.config}>
                <FormLabel htmlFor="bi-config">Configuração de BI</FormLabel>
                <Select
                  id="bi-config"
                  value={form.id_power_bi_configs}
                  onChange={bindField('id_power_bi_configs')}
                  placeholder="Selecione"
                >
                  {configs.map(config => (
                    <option key={config.id} value={config.id}>
                      {config.pbiUsername}
                    </option>
                  ))}
                </Select>
                <FormHelperText>
                  Conta do Power BI usada para abrir o relatório.
                </FormHelperText>
                <FormErrorMessage>{errors.config}</FormErrorMessage>
              </FormControl>

              <Box>
                <Button size="xs" variant="ghost" onClick={technical.onToggle}>
                  {technical.isOpen
                    ? 'Ocultar dados técnicos'
                    : 'Ver dados técnicos'}
                </Button>
                <Collapse in={technical.isOpen} animateOpacity>
                  <Stack spacing="3" mt="3">
                    <FormControl>
                      <FormLabel htmlFor="bi-group-id" fontSize="sm">
                        Workspace (groupId)
                      </FormLabel>
                      <Input
                        id="bi-group-id"
                        size="sm"
                        value={form.groupId}
                        onChange={bindField('groupId')}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel htmlFor="bi-report-id" fontSize="sm">
                        Relatório (reportId)
                      </FormLabel>
                      <Input
                        id="bi-report-id"
                        size="sm"
                        value={form.embededId}
                        onChange={bindField('embededId')}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel htmlFor="bi-embed-url" fontSize="sm">
                        URL de embed
                      </FormLabel>
                      <Input
                        id="bi-embed-url"
                        size="sm"
                        value={form.embededUrl}
                        onChange={bindField('embededUrl')}
                      />
                    </FormControl>
                    <Text fontSize="xs" color="gray.500">
                      Preenchidos a partir do link. Só altere se souber o que
                      está fazendo.
                    </Text>
                  </Stack>
                </Collapse>
              </Box>
            </Section>

            <Section
              title="Como aparece no catálogo"
              description="Nome, grupo e o card que os usuários veem na página de relatórios."
            >
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing="4"
                alignItems="start"
              >
                <Stack spacing="4">
                  <FormControl isInvalid={!!errors.reportName}>
                    <FormLabel htmlFor="bi-name">Nome</FormLabel>
                    <Input
                      id="bi-name"
                      value={form.reportName}
                      onChange={bindField('reportName')}
                      maxLength={150}
                    />
                    <FormErrorMessage>{errors.reportName}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.group}>
                    <FormLabel htmlFor="bi-group">Grupo (setor)</FormLabel>
                    <Input
                      id="bi-group"
                      list="bi-groups"
                      value={form.groupNameSamasc}
                      onChange={bindField('groupNameSamasc')}
                      placeholder="Escolha ou digite um novo"
                      maxLength={80}
                    />
                    <datalist id="bi-groups">
                      {groups.map(group => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </datalist>
                    <FormErrorMessage>{errors.group}</FormErrorMessage>
                  </FormControl>

                  <FormControl>
                    <FormLabel htmlFor="bi-order">Ordem no grupo</FormLabel>
                    <Input
                      id="bi-order"
                      type="number"
                      min={0}
                      max={9999}
                      value={form.display_order}
                      onChange={bindField('display_order')}
                    />
                    <FormHelperText>
                      Menor número aparece primeiro.
                    </FormHelperText>
                  </FormControl>
                </Stack>

                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb="2">
                    Prévia do card
                  </Text>
                  <Box pointerEvents="none" aria-hidden>
                    <ReportCard
                      report={preview}
                      appearance={appearance}
                      showGroup
                    />
                  </Box>
                </Box>
              </SimpleGrid>

              <FormControl>
                <FormLabel htmlFor="bi-summary">Descrição curta</FormLabel>
                <Input
                  id="bi-summary"
                  value={form.summary}
                  onChange={bindField('summary')}
                  maxLength={SUMMARY_MAX}
                  placeholder="Ex.: Faturamento diário por filial e produto"
                />
                <FormHelperText>
                  Aparece no card ({form.summary.length}/{SUMMARY_MAX}). Em
                  branco, o card usa o início de &quot;O que o relatório
                  mostra&quot;.
                </FormHelperText>
              </FormControl>

              <AppearancePicker
                color={form.card_color}
                icon={form.card_icon}
                previewColor={appearance.color}
                allowInherit
                onChange={handleAppearanceChange}
              />
            </Section>

            <Section
              title="Quem pode ver"
              description="As mesmas regras de sempre: o usuário precisa ter todas as permissões escolhidas."
            >
              <FormControl>
                <FormLabel>Permissões</FormLabel>
                {permissionsState === 'loading' && <Spinner size="sm" />}
                {permissionsState === 'error' && (
                  <Flex align="center" gridGap="3">
                    <Text fontSize="sm" color="red.400">
                      Não foi possível carregar as permissões deste relatório.
                    </Text>
                    <Button size="xs" onClick={handleRetryPermissions}>
                      Tentar novamente
                    </Button>
                  </Flex>
                )}
                {permissionsState === 'ready' && (
                  <MultiSelectContainer theme={toggleTheme}>
                    <MultiSelect
                      options={permissionOptions}
                      value={selectedPermissionOptions}
                      onChange={setSelectedPermissions}
                      labelledBy="Permissões"
                      overrideStrings={Internationalization}
                    />
                  </MultiSelectContainer>
                )}
                <FormHelperText>
                  Sem permissão vinculada, todos os usuários veem o relatório.
                  Com mais de uma, o usuário precisa ter todas.
                </FormHelperText>
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <Switch
                  id="bi-status"
                  isChecked={form.status}
                  onChange={event => update('status', event.target.checked)}
                  mr="3"
                />
                <FormLabel htmlFor="bi-status" mb="0">
                  {form.status
                    ? 'Ativo: aparece para os usuários'
                    : 'Inativo: escondido dos usuários'}
                </FormLabel>
              </FormControl>
            </Section>

            <Section
              title="Informações para quem usa"
              description="Aparecem no botão de informações ao abrir o relatório."
            >
              <FormControl>
                <FormLabel htmlFor="bi-description">
                  O que o relatório mostra
                </FormLabel>
                <Textarea
                  id="bi-description"
                  value={form.description}
                  onChange={bindField('description')}
                  maxLength={4000}
                  rows={3}
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
                <FormControl>
                  <FormLabel htmlFor="bi-owner">Responsável</FormLabel>
                  <Select
                    id="bi-owner"
                    value={form.owner_id}
                    onChange={bindField('owner_id')}
                    placeholder="Sem responsável"
                  >
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </Select>
                  <FormHelperText>
                    Recebe as dúvidas e resolve os comentários.
                  </FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel htmlFor="bi-refresh">
                    Atualização dos dados
                  </FormLabel>
                  <Input
                    id="bi-refresh"
                    value={form.refresh_frequency}
                    onChange={bindField('refresh_frequency')}
                    placeholder="Ex.: Diária, às 6h"
                    maxLength={120}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel htmlFor="bi-glossary">Termos do relatório</FormLabel>
                <Textarea
                  id="bi-glossary"
                  value={form.glossary}
                  onChange={bindField('glossary')}
                  maxLength={8000}
                  rows={3}
                  placeholder={
                    'Um termo por linha.\nEx.: Controle — número interno da nota'
                  }
                />
              </FormControl>
            </Section>
          </Stack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor="gray.600">
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            type="submit"
            isLoading={saving}
            isDisabled={permissionsState !== 'ready'}
          >
            {report ? 'Salvar' : 'Publicar'}
          </Button>
        </ModalFooter>
      </Box>
    </ModalComponent>
  );
}
